import {
  Bell,
  BellRing,
  FileBarChart2,
  FileSearch2,
  FileText,
  Landmark,
  Library,
  LucideIcon,
  Mail,
  ShieldCheck,
  SquareKanban,
  Zap,
} from "lucide-react";
import {
  INotificationViewAction,
  INotificationListData,
  INotificationListView,
  NotificationProduct,
  NotificationSeverity,
} from "../../_services/interface/notifications.interface";
import { brasiliaDateParts } from "../../utils/dates";

/**
 * GEN-4101 — metadados visuais do aviso "pronto para exibir" (sino, lista e leitura).
 *
 * Ícones = os MESMOS da sidebar (o aviso aponta visualmente para o módulo de onde veio). Cores de
 * urgência = a tríade texto/fundo/borda do `CredentialStatusBadge` (erro, atenção) e o neutro do tema
 * para o informativo — não há token semântico de urgência no tema, e inventar um tom "parecido"
 * divergiria das outras telas.
 */

export interface ProductMeta {
  label: string;
  icon: LucideIcon;
}

export const PRODUCT_META: Record<NotificationProduct, ProductMeta> = {
  certificates: { label: "Certidões", icon: FileText },
  dte: { label: "Caixa Postal", icon: Mail },
  divida_ativa: { label: "Dívida Ativa", icon: Landmark },
  tax_current_account: { label: "Conta Corrente", icon: FileSearch2 },
  activities: { label: "Kanban", icon: SquareKanban },
  credentials: { label: "Credenciais", icon: ShieldCheck },
  registry: { label: "Base Cadastral", icon: Library },
  alerts: { label: "Alertas", icon: BellRing },
  automations: { label: "Automações", icon: Zap },
  reports: { label: "Relatórios", icon: FileBarChart2 },
  platform: { label: "Plataforma", icon: Bell },
};

/** Ordem do filtro "Produto" (a mesma da sidebar). */
export const PRODUCT_ORDER: NotificationProduct[] = [
  "certificates",
  "divida_ativa",
  "dte",
  "tax_current_account",
  "registry",
  "credentials",
  "activities",
  "alerts",
  "automations",
  "reports",
  "platform",
];

export interface SeverityMeta {
  label: string;
  /** Explicação curta (tooltip e filtro). */
  description: string;
  color: string;
  bg: string;
  border: string;
}

export const SEVERITY_META: Record<NotificationSeverity, SeverityMeta> = {
  critical: {
    label: "Crítico",
    description: "Exige ação para não perder prazo ou ter prejuízo",
    color: "#DC2626",
    bg: "#FEF2F2",
    border: "#FECACA",
  },
  attention: {
    label: "Atenção",
    description: "Vai exigir ação em breve",
    color: "#B45309",
    bg: "#FFFBEB",
    border: "#FDE68A",
  },
  info: {
    label: "Informativo",
    description: "Para saber, sem ação imediata",
    color: "#46464A",
    bg: "#F2F0F4",
    border: "#E2E8F0",
  },
};

export const SEVERITY_ORDER: NotificationSeverity[] = ["critical", "attention", "info"];

export const metaDoProduto = (product: string | null | undefined): ProductMeta =>
  (product && (PRODUCT_META as Record<string, ProductMeta>)[product]) || PRODUCT_META.platform;

export const metaDaUrgencia = (severity: string | null | undefined): SeverityMeta =>
  (severity && (SEVERITY_META as Record<string, SeverityMeta>)[severity]) || SEVERITY_META.info;

/**
 * View do item com fallback para backend antigo (sem `view`): título = assunto, informativo, sem
 * destino. Garante que sino e lista nunca quebram durante o deploy escalonado BE/FE.
 */
export const acaoSegura = (action: INotificationViewAction | null | undefined): INotificationViewAction | null => {
  if (!action) return null;
  if (!action.external) return caminhoInternoSeguro(action.link) ? action : null;
  if (!/^https?:\/\//i.test(action.link)) return null;
  try {
    const url = new URL(action.link);
    return (url.protocol === "https:" || url.protocol === "http:") && url.hostname ? action : null;
  } catch {
    return null;
  }
};

export const viewDoItem = (item: INotificationListData): INotificationListView => {
  if (item.view) {
    const action = acaoSegura(item.view.action);
    return action === item.view.action ? item.view : { ...item.view, action };
  }
  return {
    kind: "generic",
    product: "platform",
    severity: "info",
    title: item.subject || "Notificação",
    summary: null,
    company: null,
    companyCount: null,
    link: null,
    action: null,
  };
};

/** "MRV Engenharia · Dívida Ativa PGE/SP" — empresa (ou "3 empresas") e o resumo. */
export const linhaDeContexto = (view: INotificationListView): string => {
  const partes: string[] = [];
  if (view.company?.name) partes.push(view.company.name);
  else if (view.companyCount && view.companyCount > 1) partes.push(`${view.companyCount} empresas`);
  if (view.summary) partes.push(view.summary);
  return partes.join(" · ");
};

export type GrupoDeData = "Hoje" | "Ontem" | "Esta semana" | "Há 2 semanas" | "Últimos 30 dias" | "Mais de 30 dias";

// GEN-4193: createdAt é MOMENTO — o dia dele é o de Brasília, não o do fuso do navegador.
const inicioDoDia = (d: Date) => {
  const { year, month, day } = brasiliaDateParts(d);
  return Date.UTC(year, month - 1, day);
};

/**
 * Períodos da lista (GEN-4101 r2, pedido do Thiago em 19/10): Hoje · Ontem · Esta semana · Há 2 semanas ·
 * Últimos 30 dias · Mais de 30 dias. Contados em dias de calendário, não em horas.
 */
export const grupoDaData = (valor: Date | string, agora: Date = new Date()): GrupoDeData => {
  const data = valor instanceof Date ? valor : new Date(valor);
  if (Number.isNaN(data.getTime())) return "Mais de 30 dias";
  const dias = Math.round((inicioDoDia(agora) - inicioDoDia(data)) / 86400000);
  if (dias <= 0) return "Hoje";
  if (dias === 1) return "Ontem";
  if (dias < 7) return "Esta semana";
  if (dias < 14) return "Há 2 semanas";
  if (dias < 30) return "Últimos 30 dias";
  return "Mais de 30 dias";
};

/** Agrupa mantendo a ordem de chegada (a lista já vem do mais novo para o mais antigo). */
export const agruparPorData = <T extends { createdAt: Date | string }>(
  itens: T[],
  agora: Date = new Date()
): { grupo: GrupoDeData; itens: T[] }[] => {
  const grupos: { grupo: GrupoDeData; itens: T[] }[] = [];
  for (const item of itens) {
    const grupo = grupoDaData(item.createdAt, agora);
    const ultimo = grupos[grupos.length - 1];
    if (ultimo && ultimo.grupo === grupo) ultimo.itens.push(item);
    else grupos.push({ grupo, itens: [item] });
  }
  return grupos;
};

/** Caminho interno seguro para navegar (o link vem do backend, mas a URL é dado não-confiável). */
export const caminhoInternoSeguro = (link: string | null | undefined): string | null => {
  if (!link || typeof link !== "string") return null;
  if (!link.startsWith("/") || link.startsWith("//") || link.includes("\\")) return null;
  return link;
};
